import { z } from "zod";

import { optionalQueryParam } from "./common.schemas";

// ============================================================================
// GOLDEN CONVERSATIONS — authoring (Prompt Lab phase 2)
//
// A golden conversation is a reference dialogue authored turn by turn against
// one agent+deployment: the author plays the end user, the agent generates
// each reply (on the authoring variant when one was named), and the author
// accepts/edits it until it is the golden answer. AGENT turns can be
// checkpoints — per-message test cases for eval runs (phase 3). These schemas
// are the single source for both the public v1 contract and the internal
// console door (same shapes, same idiom as the emulator's).
// ============================================================================

export const GoldenConversationStatusSchema = z.enum(["DRAFT", "READY", "ARCHIVED"]);

export const GoldenTurnRoleSchema = z.enum(["USER", "AGENT"]);

/// One tool invocation as recorded at authoring: display + judge context only —
/// at eval time tools re-execute live. `input`/`output` are whatever the model
/// sent and the tool returned; no shape is promised beyond the name.
export const GoldenRecordedToolCallSchema = z.object({
  name: z.string(),
  input: z.unknown().optional(),
  output: z.unknown().optional()
});

/// One custom judge criterion attached to a checkpoint.
export const GoldenCheckpointCriterionSchema = z.object({
  name: z.string().min(1),
  rubric: z.string().min(1),
  weight: z.number().positive().optional()
});

export const GoldenTurnSchema = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  /// 0-based position; unique and contiguous per conversation.
  index: z.number().int(),
  role: GoldenTurnRoleSchema,
  content: z.string(),
  toolCalls: GoldenRecordedToolCallSchema.array().nullable(),
  /// The agent turn was hand-edited rather than accepted as generated.
  edited: z.boolean(),
  /// AGENT turns only; defaults ON when a turn is accepted.
  isCheckpoint: z.boolean(),
  criteria: GoldenCheckpointCriterionSchema.array().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export const GoldenConversationSchema = z.object({
  id: z.string().uuid(),
  agentId: z.string().uuid(),
  deploymentId: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  status: GoldenConversationStatusSchema,
  /// The version generation runs under (tip of the authoring variant at
  /// creation). Null = the live prompt, no override.
  authoringVersionId: z.string().uuid().nullable(),
  /// True while a generated candidate awaits accept/regenerate.
  hasPendingCandidate: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export const GoldenConversationDetailSchema = GoldenConversationSchema.extend({
  turns: GoldenTurnSchema.array()
});

// ---------------------------------------------------------------------------
// Path params. `conversationId` is deliberately NOT `.uuid()`: an unknown or
// malformed id must answer "not found", not a format error (the read adapter
// shape-guards before the @db.Uuid compare). Same precedent as
// `CreatePromptVariantBodySchema.fromVersionId`.
// ---------------------------------------------------------------------------

export const GoldenConversationIdParamSchema = z.object({
  conversationId: z.string().min(1)
});

export const GoldenConversationIdAndTurnIndexParamSchema = z.object({
  conversationId: z.string().min(1),
  /// Path segments arrive as strings; the index is a 0-based turn position.
  index: z.coerce.number().int().min(0)
});

// ---------------------------------------------------------------------------
// Create / list
// ---------------------------------------------------------------------------

export const CreateGoldenConversationBodySchema = z
  .object({
    agentId: z.string().uuid(),
    deploymentId: z.string().uuid(),
    title: z.string().trim().min(1).max(200),
    description: z.string().max(2000).optional(),
    /// Variant whose TIP becomes the authoring version: a name, a variant id,
    /// or "main". Omitted = author against the live prompt.
    variant: z.string().trim().min(1).optional()
  })
  .strict();

export const CreateGoldenConversationResponseSchema = GoldenConversationSchema;

export const ListGoldenConversationsParamsSchema = z.object({
  /// `?agentId=` (empty value) means "no filter", not a format error.
  agentId: optionalQueryParam(z.string().uuid())
});

export const ListGoldenConversationsResponseSchema = z.array(GoldenConversationSchema);

export const GetGoldenConversationResponseSchema = GoldenConversationDetailSchema;

export const DeleteGoldenConversationResponseSchema = z.object({
  deleted: z.literal(true)
});

// ---------------------------------------------------------------------------
// Authoring: add-user / generate / accept
// ---------------------------------------------------------------------------

export const AddGoldenUserTurnBodySchema = z
  .object({
    text: z.string().min(1)
  })
  .strict();

export const AddGoldenUserTurnResponseSchema = GoldenTurnSchema;

/// The candidate a generate produced. `sessionId` names the ephemeral emulator
/// session it was generated in — fresh per call, so two generates never share
/// one (that IS the regenerate contract).
export const GoldenCandidateSchema = z.object({
  content: z.string(),
  toolCalls: GoldenRecordedToolCallSchema.array(),
  sessionId: z.string().uuid(),
  latencyMs: z.number().nullable(),
  tokensUsed: z.object({ input: z.number(), output: z.number(), total: z.number() }).nullable()
});

export const GenerateGoldenCandidateResponseSchema = z.object({
  conversationId: z.string().uuid(),
  sessionId: z.string().uuid(),
  candidate: GoldenCandidateSchema
});

export const AcceptGoldenCandidateBodySchema = z
  .object({
    /// Present = accept-with-edit: this text is stored as the golden content
    /// and the turn flagged `edited`. Absent = accept as generated.
    content: z.string().min(1).optional()
  })
  .strict();

export const AcceptGoldenCandidateResponseSchema = GoldenTurnSchema;

// ---------------------------------------------------------------------------
// Curation: set-golden / checkpoint / ready
// ---------------------------------------------------------------------------

export const SetGoldenTurnContentBodySchema = z
  .object({
    content: z.string().min(1)
  })
  .strict();

export const SetGoldenTurnContentResponseSchema = GoldenTurnSchema;

export const SetGoldenCheckpointBodySchema = z
  .object({
    isCheckpoint: z.boolean(),
    /// Omitted = leave stored criteria untouched; null = clear; a list
    /// replaces. Meaningful mostly with `isCheckpoint: true`.
    criteria: GoldenCheckpointCriterionSchema.array().nullable().optional()
  })
  .strict();

export const SetGoldenCheckpointResponseSchema = GoldenTurnSchema;

export const ReadyGoldenConversationResponseSchema = GoldenConversationSchema;
